import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { Trash2, Plus, Search, Package, FileText } from 'lucide-react';
import {
  fetchItems,
  fetchBranches,
  fetchSuppliers,
  createSupplierInvoiceWithItems
} from '@/api';
import type { Supplier } from '@/types/supplier';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { format, addDays } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface AddInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  suppliers: Supplier[];
}

interface Item {
  id: string;
  name: string;
  sku: string;
  buying_price: number;
  selling_price: number;
  category?: string;
  quantity: number;
}

interface Branch {
  id: string;
  name: string;
  is_main?: boolean;
}

interface InvoiceLineItem {
  id: string;
  item_id?: string;
  item_name: string;
  item_sku: string;
  quantity: number;
  buy_price: number;
  sell_price: number;
  total: number;
  is_new: boolean;
}

export function AddInvoiceDialog({ open, onOpenChange, onSuccess, suppliers }: AddInvoiceDialogProps) {
  const currencySymbol = useCurrencySymbol();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  // Invoice header fields
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dueDate, setDueDate] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [notes, setNotes] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);

  // Item search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // New item form
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemSku, setNewItemSku] = useState('');
  const [newItemBuyPrice, setNewItemBuyPrice] = useState(0);
  const [newItemSellPrice, setNewItemSellPrice] = useState(0);
  const [newItemQuantity, setNewItemQuantity] = useState(1);

  // Load items and branches on mount
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  // Auto-calculate due date based on supplier payment terms
  useEffect(() => {
    if (supplierId && invoiceDate) {
      const supplier = suppliers.find(s => s.id === supplierId);
      if (supplier) {
        const days = supplier.paymentTerm === '15days' ? 15 : supplier.paymentTerm === '30days' ? 30 : 0;
        setDueDate(format(addDays(new Date(invoiceDate), days), 'yyyy-MM-dd'));
      }
    }
  }, [supplierId, invoiceDate, suppliers]);

  const loadData = async () => {
    try {
      const [itemsRes, branchesRes] = await Promise.all([
        fetchItems(),
        fetchBranches()
      ]);
      // Parse numeric fields from API response
      const parsedItems = (itemsRes.data || []).map((item: any) => ({
        ...item,
        buying_price: parseFloat(item.buying_price) || 0,
        selling_price: parseFloat(item.selling_price) || 0,
        quantity: parseInt(item.quantity, 10) || 0
      }));
      setItems(parsedItems);
      setBranches(branchesRes.data || []);

      // Set default branch (main branch or first branch)
      const mainBranch = branchesRes.data.find((b: Branch) => b.is_main);
      if (mainBranch) {
        setSelectedBranch(mainBranch.id);
      } else if (branchesRes.data.length > 0) {
        setSelectedBranch(branchesRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load items and branches');
    }
  };

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items.slice(0, 20);
    const term = searchTerm.toLowerCase();
    return items.filter(item =>
      item.name.toLowerCase().includes(term) ||
      item.sku.toLowerCase().includes(term) ||
      (item.category && item.category.toLowerCase().includes(term))
    ).slice(0, 20);
  }, [items, searchTerm]);

  // Calculate totals
  const invoiceTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + item.total, 0);
  }, [lineItems]);

  // Add existing item to line items
  const handleAddExistingItem = (item: Item) => {
    // Check if item already exists in line items
    const existingIndex = lineItems.findIndex(li => li.item_id === item.id);
    if (existingIndex >= 0) {
      // Increase quantity
      const updated = [...lineItems];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].total = updated[existingIndex].quantity * updated[existingIndex].buy_price;
      setLineItems(updated);
    } else {
      // Add new line item
      const newLineItem: InvoiceLineItem = {
        id: crypto.randomUUID(),
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku,
        quantity: 1,
        buy_price: item.buying_price,
        sell_price: item.selling_price,
        total: item.buying_price,
        is_new: false
      };
      setLineItems([...lineItems, newLineItem]);
    }
    setSearchOpen(false);
    setSearchTerm('');
  };

  // Add new item to line items
  const handleAddNewItem = () => {
    if (!newItemName.trim()) {
      toast.error('Item name is required');
      return;
    }
    if (newItemQuantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    const newLineItem: InvoiceLineItem = {
      id: crypto.randomUUID(),
      item_name: newItemName.trim(),
      item_sku: newItemSku.trim() || `NEW-${Date.now()}`,
      quantity: newItemQuantity,
      buy_price: newItemBuyPrice,
      sell_price: newItemSellPrice,
      total: newItemQuantity * newItemBuyPrice,
      is_new: true
    };
    setLineItems([...lineItems, newLineItem]);

    // Reset form
    setNewItemName('');
    setNewItemSku('');
    setNewItemBuyPrice(0);
    setNewItemSellPrice(0);
    setNewItemQuantity(1);
    setShowNewItemForm(false);
  };

  // Update line item
  const handleUpdateLineItem = (id: string, field: keyof InvoiceLineItem, value: number | string) => {
    const updated = lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'buy_price') {
          updatedItem.total = (field === 'quantity' ? value as number : item.quantity) *
                             (field === 'buy_price' ? value as number : item.buy_price);
        }
        return updatedItem;
      }
      return item;
    });
    setLineItems(updated);
  };

  // Remove line item
  const handleRemoveLineItem = (id: string) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  // Generate PDF without selling price
  const generateInvoicePDF = async () => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (!supplier) return;

    const doc = new jsPDF();
    let yOffset = 15;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('SUPPLIER INVOICE', 105, yOffset, { align: 'center' });
    yOffset += 15;

    // Invoice details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${invoiceNumber}`, 14, yOffset);
    doc.text(`Date: ${invoiceDate}`, 140, yOffset);
    yOffset += 6;
    doc.text(`Supplier: ${supplier.name}`, 14, yOffset);
    doc.text(`Due Date: ${dueDate}`, 140, yOffset);
    yOffset += 6;
    if (supplier.phone) {
      doc.text(`Phone: ${supplier.phone}`, 14, yOffset);
      yOffset += 6;
    }
    if (supplier.address) {
      doc.text(`Address: ${supplier.address}`, 14, yOffset);
      yOffset += 6;
    }
    yOffset += 5;

    // Items table (without selling price)
    const tableData = lineItems.map(item => [
      item.item_name,
      item.item_sku,
      item.quantity.toString(),
      `${currencySymbol}${item.buy_price.toFixed(2)}`,
      `${currencySymbol}${item.total.toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: yOffset,
      head: [['Item Name', 'SKU', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [66, 66, 66] },
      foot: [['', '', '', 'Grand Total:', `${currencySymbol}${invoiceTotal.toFixed(2)}`]],
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    // Save PDF
    doc.save(`Invoice-${invoiceNumber}.pdf`);
    toast.success('PDF generated successfully');
  };

  // Submit invoice
  const handleSubmit = async () => {
    // Validation
    if (!supplierId) {
      toast.error('Please select a supplier');
      return;
    }
    if (!invoiceNumber.trim()) {
      toast.error('Invoice number is required');
      return;
    }
    if (!invoiceDate) {
      toast.error('Invoice date is required');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required');
      return;
    }
    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('supplier_id', supplierId);
      formData.append('invoice_number', invoiceNumber);
      formData.append('date', invoiceDate);
      formData.append('due_date', dueDate);
      formData.append('amount', invoiceTotal.toString());
      formData.append('branch_id', selectedBranch);
      formData.append('notes', notes);
      formData.append('items', JSON.stringify(lineItems.map(item => ({
        item_id: item.item_id || null,
        item_name: item.item_name,
        item_sku: item.item_sku,
        quantity: item.quantity,
        buy_price: item.buy_price,
        sell_price: item.sell_price,
        total_price: item.total,
        is_new: item.is_new
      }))));

      if (attachment) {
        formData.append('attachment', attachment);
      }

      await createSupplierInvoiceWithItems(formData);

      toast.success('Invoice created successfully!');
      resetForm();
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to create invoice:', error);
      toast.error(error.response?.data?.error || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSupplierId('');
    setInvoiceNumber('');
    setInvoiceDate(format(new Date(), 'yyyy-MM-dd'));
    setDueDate('');
    setSelectedBranch('');
    setAttachment(null);
    setNotes('');
    setLineItems([]);
    setSearchTerm('');
    setShowNewItemForm(false);
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${currencySymbol}${(num || 0).toFixed(2)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Add Supplier Invoice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invoice Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Supplier *</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {supplierId && (
                <p className="text-xs text-gray-500 mt-1">
                  Payment Terms: {suppliers.find(s => s.id === supplierId)?.paymentTerm || 'N/A'}
                </p>
              )}
            </div>

            <div>
              <Label>Invoice Number *</Label>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="INV-001"
              />
            </div>

            <div>
              <Label>Branch *</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger>
                  <SelectValue placeholder="Select branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name} {branch.is_main && '(Main)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Invoice Date *</Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>

            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            <div>
              <Label>Attachment</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          {/* Items Section */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Invoice Items
              </h3>
              <Badge variant="outline" className="text-lg px-3 py-1">
                Total: {formatCurrency(invoiceTotal)}
              </Badge>
            </div>

            {/* Add Item Controls */}
            <div className="flex gap-2 mb-4">
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <Search className="h-4 w-4 mr-2" />
                    Search Existing Item
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search items by name, SKU..."
                      value={searchTerm}
                      onValueChange={setSearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>No items found.</CommandEmpty>
                      <CommandGroup heading="Items">
                        {filteredItems.map(item => (
                          <CommandItem
                            key={item.id}
                            onSelect={() => handleAddExistingItem(item)}
                            className="cursor-pointer"
                          >
                            <div className="flex justify-between w-full">
                              <div>
                                <span className="font-medium">{item.name}</span>
                                <span className="text-gray-500 ml-2">({item.sku})</span>
                              </div>
                              <span className="text-gray-600">{formatCurrency(item.buying_price)}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Button
                variant="default"
                onClick={() => setShowNewItemForm(!showNewItemForm)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Item
              </Button>
            </div>

            {/* New Item Form */}
            {showNewItemForm && (
              <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-200">
                <h4 className="font-medium mb-3 text-blue-800">Add New Item</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Item Name *</Label>
                    <Input
                      placeholder="Enter item name"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">SKU</Label>
                    <Input
                      placeholder="SKU (optional)"
                      value={newItemSku}
                      onChange={(e) => setNewItemSku(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItemQuantity}
                      onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Buy Price *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newItemBuyPrice}
                      onChange={(e) => setNewItemBuyPrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Sell Price *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newItemSellPrice}
                      onChange={(e) => setNewItemSellPrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddNewItem} className="w-full">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Line Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[250px]">Item</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-center w-[100px]">Qty</TableHead>
                    <TableHead className="text-right w-[120px]">Buy Price</TableHead>
                    <TableHead className="text-right w-[120px]">Sell Price</TableHead>
                    <TableHead className="text-right w-[120px]">Total</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No items added. Search for existing items or add new ones.
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {item.is_new && (
                              <Badge variant="secondary" className="text-xs">New</Badge>
                            )}
                            <span className="font-medium">{item.item_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{item.item_sku}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            className="w-20 mx-auto text-center"
                            value={item.quantity}
                            onChange={(e) => handleUpdateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-24 ml-auto text-right"
                            value={item.buy_price}
                            onChange={(e) => handleUpdateLineItem(item.id, 'buy_price', parseFloat(e.target.value) || 0)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-24 ml-auto text-right"
                            value={item.sell_price}
                            onChange={(e) => handleUpdateLineItem(item.id, 'sell_price', parseFloat(e.target.value) || 0)}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(item.total)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveLineItem(item.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
            />
          </div>
        </div>

        <DialogFooter className="mt-6 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          {lineItems.length > 0 && (
            <Button
              variant="secondary"
              onClick={generateInvoicePDF}
              disabled={loading || !supplierId || !invoiceNumber}
            >
              <FileText className="h-4 w-4 mr-2" />
              Preview PDF
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
